import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Group, Panel, Separator } from 'react-resizable-panels'
import { DisplayWindow, DisplayHeader, Display } from '../display'
import PdfViewer from '../pdf/PdfViewer'
import RuleBuilder from './RuleBuilder'
import ResultsList from './ResultsList'
import { runTextSearch, updateTextSearch } from '../../services/textSearches'
import { standardPdfUrl } from '../../services/pdfs'
import { standardId, standardName } from '../../utils/standardRef'
import './GlobalTextSearchDisplay.css'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconChevrons = ({ dir }) => (
  <svg {...svgProps}>
    {dir === 'right' ? (
      <>
        <polyline points="7 6 13 12 7 18" />
        <polyline points="13 6 19 12 13 18" />
      </>
    ) : (
      <>
        <polyline points="17 6 11 12 17 18" />
        <polyline points="11 6 5 12 11 18" />
      </>
    )}
  </svg>
)

const EMPTY_RULE = { text: '', caseSensitive: false, wholeWords: false }

function normRules(rules) {
  return (rules || []).map((r) => ({
    text: r.text || '',
    caseSensitive: Boolean(r.caseSensitive),
    wholeWords: Boolean(r.wholeWords),
  }))
}

// Build the editable draft from a saved search. Always show at least one Find
// row so the builder isn't empty on a fresh search.
function draftFromSearch(search) {
  const findRules = normRules(search?.findRules)
  return {
    findRuleLogic: search?.findRuleLogic || 'or',
    exemptRuleLogic: search?.exemptRuleLogic || 'or',
    findRules: findRules.length ? findRules : [{ ...EMPTY_RULE }],
    exemptRules: normRules(search?.exemptRules),
  }
}

// Signature of the savable parts of a draft (empty rules ignored) for dirty checks.
function ruleSig(rules) {
  return rules
    .filter((r) => r.text.trim().length > 0)
    .map((r) => [r.text.trim(), r.caseSensitive, r.wholeWords])
}
function draftSig(draft) {
  return JSON.stringify({
    fl: draft.findRuleLogic,
    el: draft.exemptRuleLogic,
    f: ruleSig(draft.findRules),
    e: ruleSig(draft.exemptRules),
  })
}

// Payload of just the rule fields, for run and save.
function rulePayload(draft) {
  return {
    findRuleLogic: draft.findRuleLogic,
    exemptRuleLogic: draft.exemptRuleLogic,
    findRules: draft.findRules.filter((r) => r.text.trim().length > 0),
    exemptRules: draft.exemptRules.filter((r) => r.text.trim().length > 0),
  }
}

// The inner builder+results | viewer split for one Global Text Search window.
// The left pane hosts the rule builder over a scrolling results list; the right
// pane is a PDF viewer that opens the page/item the user clicks. Split sizes
// persist per window via `onPanesChange`, mirroring GlobalSearchPanes.
function TextSearchPanes({ search, panes, onPanesChange, onSaved }) {
  const searchId = search?._id
  const defaultLayout = useMemo(
    () => ({ builder: panes?.builder ?? 45, viewer: panes?.viewer ?? 55 }),
    [panes]
  )

  const [draft, setDraft] = useState(() => draftFromSearch(search))
  const savedSigRef = useRef(draftSig(draft))
  const [results, setResults] = useState({ status: 'idle', results: [], hasMore: false, error: '' })
  const [running, setRunning] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [saving, setSaving] = useState(false)
  const cursorRef = useRef(null)
  const [viewerItem, setViewerItem] = useState(null)
  // The Find rules from the last run, used to highlight matches in the viewer.
  // Held separately from `draft` so edits made after running don't change the
  // highlights until the next run.
  const [highlightRules, setHighlightRules] = useState([])

  // Reset everything when the window switches to a different saved search.
  useEffect(() => {
    const next = draftFromSearch(search)
    setDraft(next)
    savedSigRef.current = draftSig(next)
    setResults({ status: 'idle', results: [], hasMore: false, error: '' })
    setViewerItem(null)
    setHighlightRules([])
    cursorRef.current = null
  }, [searchId]) // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = draftSig(draft) !== savedSigRef.current

  const run = useCallback(async () => {
    setRunning(true)
    setResults((prev) => ({ ...prev, status: 'loading', error: '' }))
    cursorRef.current = null
    try {
      const payload = rulePayload(draft)
      const data = await runTextSearch(payload)
      cursorRef.current = data.nextCursor
      setHighlightRules(payload.findRules)
      setResults({ status: 'ready', results: data.results, hasMore: data.hasMore, error: '' })
    } catch (err) {
      setResults({ status: 'error', results: [], hasMore: false, error: err.message || 'Search failed.' })
    } finally {
      setRunning(false)
    }
  }, [draft])

  const loadMore = useCallback(async () => {
    if (!cursorRef.current) return
    setLoadingMore(true)
    try {
      const data = await runTextSearch({ ...rulePayload(draft), cursor: cursorRef.current })
      cursorRef.current = data.nextCursor
      setResults((prev) => ({
        status: 'ready',
        results: [...prev.results, ...data.results],
        hasMore: data.hasMore,
        error: '',
      }))
    } catch {
      // keep existing results on a load-more failure
    } finally {
      setLoadingMore(false)
    }
  }, [draft])

  const save = useCallback(async () => {
    if (!searchId) return
    setSaving(true)
    try {
      const updated = await updateTextSearch(searchId, rulePayload(draft))
      savedSigRef.current = draftSig(draft)
      onSaved?.(updated)
    } catch {
      // leave the draft dirty so the user can retry
    } finally {
      setSaving(false)
    }
  }, [searchId, draft, onSaved])

  const openPage = useCallback((result) => {
    if (!result?.standard) return
    setViewerItem({ standard: result.standard, page: result.page })
  }, [])
  const openItem = useCallback((item) => {
    if (!item?.standard) return
    setViewerItem({ standard: item.standard, page: item.pageStart })
  }, [])

  const builderPanelRef = useRef(null)
  const viewerPanelRef = useRef(null)
  const [collapsed, setCollapsed] = useState('none') // 'none' | 'builder' | 'viewer'

  const onBuilderResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'builder' : c === 'builder' ? 'none' : c))
  }, [])
  const onViewerResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'viewer' : c === 'viewer' ? 'none' : c))
  }, [])

  const toggleBuilderMaximize = () => {
    if (collapsed === 'viewer') viewerPanelRef.current?.expand()
    else viewerPanelRef.current?.collapse()
  }
  const toggleViewerMaximize = () => {
    if (collapsed === 'builder') builderPanelRef.current?.expand()
    else builderPanelRef.current?.collapse()
  }

  return (
    <div className="gs-display">
      <Group
        orientation="horizontal"
        className="gs-panelgroup"
        defaultLayout={defaultLayout}
        onLayoutChanged={onPanesChange}
      >
        <Panel
          id="builder"
          className="gs-pane"
          defaultSize="45%"
          minSize="25%"
          collapsible
          collapsedSize="0%"
          panelRef={builderPanelRef}
          onResize={onBuilderResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                title="Search"
                actions={
                  <button
                    type="button"
                    title={collapsed === 'viewer' ? 'Restore split view' : 'Expand search pane'}
                    aria-label={collapsed === 'viewer' ? 'Restore split view' : 'Expand search pane'}
                    className={collapsed === 'viewer' ? 'is-active' : ''}
                    onClick={toggleBuilderMaximize}
                  >
                    <IconChevrons dir={collapsed === 'viewer' ? 'left' : 'right'} />
                  </button>
                }
              />
            }
          >
            <Display fill>
              <div className="ts-left">
                <RuleBuilder
                  draft={draft}
                  onChange={setDraft}
                  onRun={run}
                  onSave={save}
                  running={running}
                  saving={saving}
                  dirty={dirty}
                />
                <div className="ts-results-scroll">
                  <ResultsList
                    state={results}
                    onOpenPage={openPage}
                    onOpenItem={openItem}
                    onLoadMore={loadMore}
                    loadingMore={loadingMore}
                  />
                </div>
              </div>
            </Display>
          </DisplayWindow>
        </Panel>

        <Separator className="gs-resize-handle" />

        <Panel
          id="viewer"
          className="gs-pane"
          defaultSize="55%"
          minSize="25%"
          collapsible
          collapsedSize="0%"
          panelRef={viewerPanelRef}
          onResize={onViewerResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                title={viewerItem ? standardName(viewerItem.standard) || 'Viewer' : 'Viewer'}
                actions={
                  <button
                    type="button"
                    title={collapsed === 'builder' ? 'Restore split view' : 'Expand viewer pane'}
                    aria-label={
                      collapsed === 'builder' ? 'Restore split view' : 'Expand viewer pane'
                    }
                    className={collapsed === 'builder' ? 'is-active' : ''}
                    onClick={toggleViewerMaximize}
                  >
                    <IconChevrons dir={collapsed === 'builder' ? 'right' : 'left'} />
                  </button>
                }
              />
            }
          >
            <Display fill>
              {viewerItem ? (
                <PdfViewer
                  key={standardId(viewerItem.standard)}
                  fileUrl={standardPdfUrl(viewerItem.standard)}
                  page={viewerItem.page}
                  highlightRules={highlightRules}
                />
              ) : (
                <div className="display-empty">
                  Click a result to view its page in the standard here.
                </div>
              )}
            </Display>
          </DisplayWindow>
        </Panel>
      </Group>
    </div>
  )
}

export default TextSearchPanes
